import React, {Children} from 'react';
import {CSSTransition, TransitionGroup} from 'react-transition-group';

import {isTestEnvironment} from '@pb/kit-util-is-test-environment';

import type {ComponentProps, FC, ReactElement, ReactNode} from 'react';


/**
 * Timeout type
 */
type Timeout<T> = {
    /**
     * Animation Enter Timeout
     */
    enter?: T,

    /**
     * Animation Leave Timeout
     */
    exit?: T,
};

/**
 * Children Props
 */
type ChildrenProps = {
    /**
     * Children Node
     */
    children: ReactNode | ReactNode[],
};
type ClassNamesType =
    | {
        appear?: string,
        appearActive?: string,
        appearDone?: string,
        enter?: string,
        enterActive?: string,
        enterDone?: string,
        exit?: string,
        exitActive?: string,
        exitDone?: string,
    }
    | string;

export interface Props extends Omit<ComponentProps<typeof CSSTransition>, 'timeout' | 'nodeRef'> {
    /**
     * CSS class name prefix or CSS Modules object used by `CSSTransition` to
     * generate transition phase classes (e.g. `enter`, `enter-active`,
     * `enter-done`, `exit`, `exit-active`, `exit-done`). Mirrors the `classNames`
     * prop of `react-transition-group`'s `CSSTransition`.
     */
    classNames: ClassNamesType,

    /**
     * Duration of the CSS transition in milliseconds. Pass a plain number to use
     * the same duration for enter and exit, or a `{ enter, exit }` object to set
     * them independently. Defaults to `300`.
     */
    timeout?: number | Timeout<number>,

    /**
     * Component used as the root element of `TransitionGroup`. Defaults to a
     * function that returns the first child, effectively rendering no extra
     * wrapper DOM node around the transition group.
     */
    component?: ComponentProps<typeof TransitionGroup>['component'],

    /**
     * Child element(s) to animate. Each child is wrapped in a `CSSTransition`
     * and animated independently when it mounts or unmounts.
     */
    children: ReactNode | ReactNode[],

    /**
     * When `true` (default), runs the enter animation when a child mounts.
     * Set to `false` to skip the enter phase entirely (child appears immediately).
     */
    enter?: boolean,

    /**
     * When `true` (default), runs the exit animation when a child unmounts.
     * Set to `false` to skip the exit phase entirely (child disappears immediately).
     */
    exit?: boolean,

    /**
     * When `true`, also runs the enter animation on the initial mount (the
     * "appear" phase). Defaults to `false` — children appear without animation
     * on first render.
     */
    appear?: boolean,

    /**
     * Callback fired after the enter (or appear) phase completes and the
     * `*-done` class has been applied to the transitioning node.
     */
    onEntered?: () => void,

    /**
     * Callback fired after the exit phase completes and the `exit-done` class
     * has been applied to the transitioning node.
     */
    onExited?: () => void,

    /**
     * Ref pointing to the transitioning DOM element. Required to avoid
     * deprecated `ReactDOM.findDOMNode` usage in `react-transition-group`.
     */
    nodeRef: React.RefObject<HTMLElement | null>,
}

const rootChild = ({children}: ChildrenProps) =>
    Children.toArray(children)[0] ?? null;

/**
 * CSS-transition wrapper around `react-transition-group` that animates child
 * elements entering and leaving the DOM using CSS class names.
 *
 * Wraps each child in a `CSSTransition` inside a `TransitionGroup`. Provide
 * a `classNames` prop (a CSS Module object or a string prefix) to control the
 * applied animation classes — e.g. `enter`, `enter-active`, `exit`, `exit-active`.
 * A `nodeRef` pointing at the transitioning DOM node is required so
 * `react-transition-group` can avoid deprecated `findDOMNode` usage.
 *
 * In test environments (`NODE_ENV === "test"`) the animation layer is bypassed
 * and children are rendered inside a plain `<span>`, preventing flaky async
 * animation behavior in unit tests.
 *
 * ## Features
 * - Per-child `enter`/`exit`/`appear` phase control via props
 * - Configurable duration via `timeout` (number or `{ enter, exit }` object)
 * - Custom root component via `component` prop (defaults to first child)
 * - Automatic test-environment passthrough to avoid animation side-effects
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-animation--documentation}
 */
export const Animation: FC<Props> = function Animation(props: Props) {
    const {
        appear = false,
        enter = true,
        exit = true,
        timeout = 300,
        component = rootChild,
        children,
        nodeRef,
        ...rest
    } = props;

    if (isTestEnvironment()) {
        return <span>{children}</span>;
    } else {
        return (
            <TransitionGroup component={component}>
                {Children.map(children, (child, i) => {
                    return (
                        child && (
                            <CSSTransition
                                key={(child as ReactElement).key ?? i}
                                appear={appear}
                                enter={enter}
                                exit={exit}
                                timeout={timeout}
                                nodeRef={nodeRef}
                                {...rest}>
                                {child}
                            </CSSTransition>
                        )
                    );
                })}
            </TransitionGroup>
        );
    }
};
